import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { toast } from "sonner";

const KIND_LABEL = {
  engine_settings: "Engine settings (financial year, tax, dividend & reserve, sign convention)",
  reconciliation_tolerance: "Reconciliation tolerance / materiality",
  fcf_methodology: "Free Cash Flow methodology",
  readiness_rules: "Level-3 readiness rules & coverage weights",
  interface_library: "Aikyam interface library",
  parameter_methodologies: "Parameter methodologies to be frozen (D/E, ROCE, days ratios)",
  evidence_bands: "Evidence scoring bands (0–10)",
  conflict_bands: "Survey vs evidence conflict thresholds",
};

const blankParam = {
  parameter_id: "", name: "", unit: "", methodology: "", metric_key: "", direction: "Higher",
  category: "Other", approval_status: "Pending Aikyam approval", notes: "", active: true,
  client_selectable: true,
};

const METRIC_KEYS = ["", "free_cash_flow", "net_profit_margin", "ebitda_margin", "debt_equity",
  "roce", "roe", "receivable_days", "inventory_days", "payable_days", "cash_conversion_cycle",
  "revenue_growth", "revenue", "ebitda", "pat", "throughput"];

export default function FpConfiguration() {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [tenants, setTenants] = useState([]);
  const [param, setParam] = useState(blankParam);
  const [showParam, setShowParam] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, t] = await Promise.all([api.get("/fp-config"), api.get("/tenants")]);
      setData(c.data);
      setTenants(t.data);
      setDrafts(Object.fromEntries(Object.entries(c.data.config)
        .map(([k, v]) => [k, JSON.stringify(v.items, null, 2)])));
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const publish = async (kind) => {
    let items;
    try { items = JSON.parse(drafts[kind]); } catch { return toast.error("That is not valid JSON"); }
    try {
      await api.put(`/fp-config/${kind}`, { items, approval_status: "Approved by Aikyam" });
      toast.success(`Published a new version of ${KIND_LABEL[kind] || kind}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveParam = async () => {
    try {
      const exists = (data.parameters || []).some((p) => p.parameter_id === param.parameter_id);
      if (exists) await api.put(`/fp-config/parameters/${param.parameter_id}`, param);
      else await api.post("/fp-config/parameters", param);
      toast.success(`Parameter ${param.parameter_id} saved`);
      setParam(blankParam);
      setShowParam(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const pilot = async (action, tenantId) => {
    try {
      if (action === "create") {
        const { data: out } = await api.post("/fp-config/pilot-data");
        toast.success(`${out.tenant_name}: pilot pack loaded (${out.datasets} statements)`);
      } else if (action === "load") {
        await api.post(`/fp-config/pilot-data/${tenantId}`);
        toast.success("Pilot pack loaded into this engagement");
      } else if (action === "reset") {
        const { data: out } = await api.post(`/fp-config/pilot-data/${tenantId}?reset=true`);
        toast.success(`Pilot pack restored · ${out.own_uploads_removed} own upload(s) removed`);
      } else {
        await api.delete(`/fp-config/pilot-data/${tenantId}`);
        toast.success("Pilot data cleared");
      }
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <p className="label-caps animate-pulse">Loading engine configuration</p>;

  return (
    <div className="space-y-8">
      <PageHeader
        testid="fp-config-header"
        eyebrow="Administration · Panchatanthra · Financial Performance"
        title="Financial engine configuration"
        description="Aikyam-controlled masters for the Level-3 Financial Performance engine. Publishing creates a new immutable version. Items marked pending are the decisions the FRD leaves to Aikyam — the defaults shown reproduce the approved pilot."
      />

      <div className="panel p-6" data-testid="fp-pilot-panel">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-lg text-navy">Illustrative pilot pack</h2>
            <p className="mt-1 max-w-2xl text-[11px] text-muted-foreground">
              Loads the April 2026 acceptance-test dataset (Trial Balance plus eight interfaces),
              its mapping master, dimension masters, budget and the five pilot-selected parameters.
              Needed once in each environment — the published app has its own database.
            </p>
          </div>
          <button onClick={() => pilot("create")} data-testid="fp-pilot-create"
                  className="rounded-full bg-terracotta px-5 py-3 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-terracotta/90">
            Create pilot engagement
          </button>
        </div>
        <div className="mt-4 divide-y divide-border">
          {tenants.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <p className="text-sm font-medium text-navy">{t.name}</p>
              <div className="flex gap-2">
                <button onClick={() => pilot("load", t.id)} data-testid={`fp-pilot-load-${t.id}`}
                        className="rounded-full bg-navy px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-white hover:bg-navy/90">
                  Load pilot pack
                </button>
                <button onClick={() => pilot("reset", t.id)} data-testid={`fp-pilot-reset-${t.id}`}
                        className="rounded-full border border-border px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-navy hover:bg-muted">
                  Reset to pilot
                </button>
                <button onClick={() => pilot("clear", t.id)} data-testid={`fp-pilot-clear-${t.id}`}
                        className="rounded-full border border-border px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-navy hover:bg-muted">
                  Clear
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="panel overflow-hidden" data-testid="fp-parameter-library">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
          <div>
            <p className="label-caps">Aikyam Financial Parameter Library</p>
            <h2 className="font-display text-lg text-navy">
              A catalogue, not a client scorecard — {data.parameters.length} parameters
            </h2>
          </div>
          <button onClick={() => { setParam(blankParam); setShowParam((v) => !v); }}
                  data-testid="fp-param-add-toggle"
                  className="rounded-full bg-terracotta px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-terracotta/90">
            {showParam ? "Close" : "Add parameter"}
          </button>
        </div>

        {showParam && (
          <div className="border-b border-border bg-muted/20 p-6" data-testid="fp-param-form">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {[["parameter_id", "Parameter ID"], ["name", "Name"], ["unit", "Unit"],
                ["methodology", "Calculation methodology"], ["category", "Category"],
                ["notes", "Notes"]].map(([key, label]) => (
                <label key={key} className="block">
                  <span className="label-caps">{label}</span>
                  <input value={param[key]} data-testid={`fp-param-${key}`}
                         onChange={(e) => setParam({ ...param, [key]: e.target.value })}
                         className="mt-1.5 w-full rounded-xl border border-border bg-card px-3 py-2 text-xs text-navy outline-none focus:border-terracotta" />
                </label>
              ))}
              <label className="block">
                <span className="label-caps">Engine metric (blank = approved manual input)</span>
                <select value={param.metric_key} data-testid="fp-param-metric-key"
                        onChange={(e) => setParam({ ...param, metric_key: e.target.value })}
                        className="mt-1.5 w-full rounded-xl border border-border bg-card px-3 py-2 text-xs text-navy outline-none focus:border-terracotta">
                  {METRIC_KEYS.map((k) => <option key={k} value={k}>{k || "— manual input —"}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="label-caps">Direction</span>
                <select value={param.direction} data-testid="fp-param-direction"
                        onChange={(e) => setParam({ ...param, direction: e.target.value })}
                        className="mt-1.5 w-full rounded-xl border border-border bg-card px-3 py-2 text-xs text-navy outline-none focus:border-terracotta">
                  {["Higher", "Lower", "Range", "Contextual"].map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </label>
              <label className="flex items-end gap-2">
                <input type="checkbox" checked={param.active} data-testid="fp-param-active"
                       onChange={(e) => setParam({ ...param, active: e.target.checked })} />
                <span className="label-caps">Active in library</span>
              </label>
            </div>
            <button onClick={saveParam} data-testid="fp-param-save"
                    className="mt-5 rounded-full bg-navy px-6 py-3 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-navy/90">
              Save parameter
            </button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left">ID</th>
                <th className="px-4 py-3 text-left">Parameter</th>
                <th className="px-4 py-3 text-left">Unit</th>
                <th className="px-4 py-3 text-left">Methodology</th>
                <th className="px-4 py-3 text-left">Direction</th>
                <th className="px-4 py-3 text-left">Approval</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.parameters.map((p) => (
                <tr key={p.parameter_id} data-testid={`fp-param-row-${p.parameter_id}`}>
                  <td className="px-4 py-2.5 font-mono text-navy">{p.parameter_id}</td>
                  <td className="px-4 py-2.5 font-medium text-navy">{p.name}</td>
                  <td className="px-4 py-2.5 text-foreground/80">{p.unit}</td>
                  <td className="px-4 py-2.5 text-foreground/70">{p.methodology}</td>
                  <td className="px-4 py-2.5 text-foreground/80">{p.direction}</td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] ${
                      /pending/i.test(p.approval_status) ? "bg-warning/25 text-[hsl(30_88%_30%)]"
                        : "bg-success/18 text-success"}`}>
                      {p.approval_status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => { setParam({ ...blankParam, ...p }); setShowParam(true); }}
                            data-testid={`fp-param-edit-${p.parameter_id}`}
                            className="rounded-full border border-border px-3 py-1 text-[9px] uppercase tracking-[0.1em] text-navy hover:bg-muted">
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {Object.entries(data.config).map(([kind, cfg]) => (
          <div key={kind} className="panel p-6" data-testid={`fp-config-${kind}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h2 className="max-w-md font-display text-base leading-snug text-navy">
                {KIND_LABEL[kind] || kind}
              </h2>
              <div className="text-right">
                <span className="label-caps">{cfg.version_label}</span>
                <p className={`mt-1 text-[10px] font-bold uppercase tracking-[0.1em] ${
                  /pending/i.test(cfg.approval_status) ? "text-[hsl(30_88%_30%)]" : "text-success"}`}>
                  {cfg.approval_status}
                </p>
              </div>
            </div>
            <textarea rows={12} value={drafts[kind] || ""} data-testid={`fp-config-input-${kind}`}
                      onChange={(e) => setDrafts({ ...drafts, [kind]: e.target.value })}
                      className="mt-3 w-full resize-y rounded-xl border border-border bg-card px-4 py-3 font-mono text-[11px] outline-none focus:border-terracotta" />
            <button onClick={() => publish(kind)} data-testid={`fp-config-publish-${kind}`}
                    className="mt-3 rounded-full bg-terracotta px-5 py-2.5 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-terracotta/90">
              Publish & approve new version
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
